import { httpStatus } from "@utils/errors";

export const IDEMPOTENCY_HEADER = "Idempotency-Key";
export const IDEMPOTENT_RETRY_DELAY_MS = 1000;
export const IDEMPOTENT_RETRY_LIMIT = 3;

export interface IdempotencyKeyParams {
  idempotencyKey: string;
}

export interface IdempotencyRandomSource {
  getRandomValues: (array: Uint8Array<ArrayBuffer>) => Uint8Array<ArrayBuffer>;
  randomUUID?: () => string;
}

const hex = (byte: number) => byte.toString(16).padStart(2, "0");

const runtimeRandomSource: IdempotencyRandomSource = {
  getRandomValues: array => globalThis.crypto.getRandomValues(array),
  randomUUID:
    typeof globalThis.crypto.randomUUID === "function"
      ? () => globalThis.crypto.randomUUID()
      : undefined,
};

export const createIdempotencyKey = (
  source: IdempotencyRandomSource = runtimeRandomSource,
): string => {
  if (typeof source.randomUUID === "function") return source.randomUUID();
  const bytes = source.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const digits = Array.from(bytes, hex).join("");
  return [
    digits.slice(0, 8),
    digits.slice(8, 12),
    digits.slice(12, 16),
    digits.slice(16, 20),
    digits.slice(20),
  ].join("-");
};

export const idempotencyHeaders = (
  params: IdempotencyKeyParams,
): Record<string, string> => ({ [IDEMPOTENCY_HEADER]: params.idempotencyKey });

const responseData = (error: unknown): Record<string, unknown> | null => {
  if (typeof error !== "object" || error === null || !("response" in error))
    return null;
  const { response } = error;
  if (typeof response !== "object" || response === null) return null;
  const data = "data" in response ? response.data : null;
  return typeof data === "object" && data !== null
    ? (data as Record<string, unknown>)
    : null;
};

export const isRetryableResponse = (
  status: number | undefined,
  data: unknown,
): boolean =>
  (status === 409 || status === 503) &&
  typeof data === "object" &&
  data !== null &&
  "retryable" in data &&
  data.retryable === true;

export const isRetryableIdempotentFailure = (error: unknown): boolean =>
  isRetryableResponse(httpStatus(error), responseData(error));

export type IdempotentConflict =
  { kind: "key_reused" } | { kind: "slot_taken" } | { kind: "changed" };

export const getIdempotentConflict = (
  error: unknown,
): IdempotentConflict | null => {
  if (httpStatus(error) !== 409 || isRetryableIdempotentFailure(error))
    return null;
  const reason = responseData(error)?.reason;
  if (reason === "key_reused") return { kind: "key_reused" };
  if (reason === "slot_taken") return { kind: "slot_taken" };
  return { kind: "changed" };
};

export const getIdempotentFailureMessage = (error: unknown): string | null => {
  const message = responseData(error)?.message;
  return typeof message === "string" && message.trim() ? message.trim() : null;
};

export const isSettledIdempotentFailure = (error: unknown): boolean => {
  const status = httpStatus(error);
  return (
    status !== undefined && status < 500 && !isRetryableIdempotentFailure(error)
  );
};

const wait = (milliseconds: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, milliseconds);
  });

export interface IdempotentRetryOptions {
  retries?: number;
  delayMs?: number;
  sleep?: (milliseconds: number) => Promise<void>;
}

export const runIdempotent = async <T>(
  send: () => Promise<T>,
  {
    retries = IDEMPOTENT_RETRY_LIMIT,
    delayMs = IDEMPOTENT_RETRY_DELAY_MS,
    sleep = wait,
  }: IdempotentRetryOptions = {},
): Promise<T> => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await send();
    } catch (error) {
      if (attempt >= retries || !isRetryableIdempotentFailure(error))
        throw error;
      await sleep(delayMs);
    }
  }
};

export interface IdempotencySession {
  keyFor: (signature: string) => string;
  settle: () => void;
}

export const createIdempotencySession = (
  createKey: () => string = createIdempotencyKey,
): IdempotencySession => {
  let pending: { signature: string; key: string } | null = null;
  return {
    keyFor: signature => {
      if (pending?.signature !== signature)
        pending = { signature, key: createKey() };
      return pending.key;
    },
    settle: () => {
      pending = null;
    },
  };
};

export const sendIdempotent = async <T>(
  session: IdempotencySession,
  operation: unknown,
  send: (idempotencyKey: string) => Promise<T>,
  options?: IdempotentRetryOptions,
): Promise<T> => {
  const idempotencyKey = session.keyFor(JSON.stringify(operation) ?? "");
  try {
    const result = await runIdempotent(() => send(idempotencyKey), options);
    session.settle();
    return result;
  } catch (error) {
    if (isSettledIdempotentFailure(error)) session.settle();
    throw error;
  }
};
